import './Ripple.scss'

import { useFn, useTimeout } from '@eturi/react'
import cls from 'classnames'
import type { CSSProperties, PointerEvent } from 'react'
import { memo, useLayoutEffect, useState } from 'react'

type RippleDot = {
	readonly id: string
	readonly size: number
	readonly x: number
	readonly y: number
}

export type RippleProps = {
	readonly className?: string
	readonly color?: string
	readonly duration?: number
}

const Ripple = ({ duration = 500, color = '#C2EEF7', className }: RippleProps) => {
	const [ripples, setRipples] = useState<RippleDot[]>([])

	const cleanup = useFn(() => setRipples([]))

	useDebouncedRippleCleanUp(ripples.length, duration, cleanup)

	const addRipple = useFn((e: PointerEvent<HTMLDivElement>) => {
		const rect = e.currentTarget.getBoundingClientRect()
		const size = Math.max(rect.width, rect.height)
		const x = e.clientX - rect.left - size / 2
		const y = e.clientY - rect.top - size / 2
		const id = `${x}-${y}-${size}-${Date.now()}`

		setRipples((prev) => [...prev, { id, x, y, size }])
	})

	return (
		<div
			className={cls('ripple-container', className)}
			onPointerDown={addRipple}
			style={
				{
					'--ripple-duration': `${duration}ms`,
					'--ripple-color': color,
				} as CSSProperties
			}
		>
			{ripples.map((r) => (
				<span
					key={r.id}
					style={{
						backgroundColor: color,
						top: r.y,
						left: r.x,
						width: r.size,
						height: r.size,
					}}
				/>
			))}
		</div>
	)
}

export default memo(Ripple)

const useDebouncedRippleCleanUp = (rippleCount: number, duration: number, cleanUp: () => void) => {
	const [setTimeout, clearTimeout] = useTimeout()

	useLayoutEffect(() => {
		if (rippleCount === 0) return
		setTimeout(cleanUp, duration * 4)
		return clearTimeout
	}, [rippleCount, duration, cleanUp])
}
